'use client'

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Sidebar } from './Sidebar/Sidebar'
import { ListingList } from './Main/ListingList'
import { FilterSort } from './FilterSort'
import { FilterSheet } from './FilterSheet'
import { ListingsHeader } from './ListingsHeader'
import { ListingPagePagination } from './pagination'
import { SIDEBAR_WIDTH, ANIMATION_DURATION } from '../constants'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import {
  useListingsQuery,
  useBrandQuery,
  useCategoriesQuery,
  useListingSizesQuery,
  useCurrentUserQuery,
  Listing,
  Brand,
  Category,
  Size,
  User,
} from '@/api'
import { useListingsStore } from '@/stores/useListingsStore'
import { useGTMTracking } from '@/hooks/useGTMTracking'

const ITEMS_PER_PAGE = 16

export const Listings = () => {
  const [isOpen, setIsOpen] = useState(false)
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const currentPage = Number(searchParams.get('page') ?? 1)
  const setLastListingsUrl = useListingsStore(
    (state) => state.setLastListingsUrl
  )

  const { data: { currentUser } = {}, loading: userLoading } =
    useCurrentUserQuery()
  const { trackEvent } = useGTMTracking()

  const [selectedGender, setSelectedGender] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedConditions, setSelectedConditions] = useState<string[]>([])
  const [selectedSizes, setSelectedSizes] = useState<string[]>([])
  const [sortOrder, setSortOrder] = useState('recommended')
  const [selectedKeyword, setSelectedKeyword] = useState('')

  const keyword = searchParams.get('keyword') || undefined

  useEffect(() => {
    setSelectedGender(searchParams.get('gender') ?? '')
    setSelectedCategories(
      searchParams.get('categories')?.split(',').filter(Boolean) ?? []
    )
    setSelectedConditions(
      searchParams.get('conditions')?.split(',').filter(Boolean) ?? []
    )
    setSelectedSizes(
      searchParams.get('sizes')?.split(',').filter(Boolean) ?? []
    )
    setSortOrder(searchParams.get('sort') ?? 'recommended')
    setSelectedKeyword(searchParams.get('keyword') ?? '')

    // 現在のURLをストアに保存
    const currentUrl = searchParams.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname
    setLastListingsUrl(currentUrl)
  }, [searchParams, pathname, setLastListingsUrl])

  const {
    data: {
      listings: { listings = [], totalCount = 0, pagination = undefined } = {},
    } = {},
  } = useListingsQuery({
    variables: {
      page: currentPage,
      perPage: ITEMS_PER_PAGE,
      brandId: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE || '',
      search: {
        sex: searchParams.get('gender') || undefined,
        categoryIds: searchParams.get('categories')?.split(',') || undefined,
        conditionIds: searchParams.get('conditions')?.split(',') || undefined,
        sizeIds: searchParams.get('sizes')?.split(',') || undefined,
        sortOrder: searchParams.get('sort') || 'recommended',
        keyword: searchParams.get('keyword') || undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
  })

  const { data: { brand } = {} } = useBrandQuery({
    variables: { uuid: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE || '' },
    fetchPolicy: 'cache-and-network',
  })

  const { data: { categories } = {} } = useCategoriesQuery({
    variables: { brandId: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE || '' },
    fetchPolicy: 'cache-and-network',
  })

  const { data: { listingSizes = [] } = {} } = useListingSizesQuery({
    variables: { brandId: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE || '' },
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (!userLoading && listings.length > 0) {
      trackEvent(
        'view_item_list',
        null,
        currentUser as User,
        null,
        null,
        null,
        listings as any
      )
    }
  }, [userLoading, currentUser, listings, trackEvent])

  const handleSortChange = (value: string) => {
    setSortOrder(value)
    const params = new URLSearchParams(searchParams)
    params.set('page', '1')
    params.set('sort', value)
    router.push(`?${params.toString()}`)
  }

  const handleClearFilters = () => {
    setSelectedGender('')
    setSelectedCategories([])
    setSelectedConditions([])
    setSelectedSizes([])
    setSelectedKeyword('')
    router.push('?page=1')
  }

  const handleApplyFilters = () => {
    const params = new URLSearchParams()
    params.set('page', '1')

    if (selectedGender) params.set('gender', selectedGender)
    if (selectedCategories.length)
      params.set('categories', selectedCategories.join(','))
    if (selectedConditions.length)
      params.set('conditions', selectedConditions.join(','))
    if (selectedSizes.length) params.set('sizes', selectedSizes.join(','))
    if (sortOrder && sortOrder !== 'recommended') params.set('sort', sortOrder)
    if (selectedKeyword) params.set('keyword', selectedKeyword)

    router.push(`?${params.toString()}`)
  }

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', page.toString())
    router.push(`?${params.toString()}`)
  }

  if (!pagination) return null

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <div className="relative flex flex-1">
        <motion.aside
          initial={{ x: '-100%' }}
          animate={{ x: isOpen ? 0 : '-100%' }}
          transition={{ duration: ANIMATION_DURATION }}
          className="absolute top-0 left-0 h-full w-[300px] bg-white z-40 hidden md:block"
        >
          <Sidebar
            brand={brand as Brand}
            categories={categories as Category[]}
            sizes={listingSizes as Size[]}
            selectedGender={selectedGender}
            selectedCategories={selectedCategories}
            selectedConditions={selectedConditions}
            selectedSizes={selectedSizes}
            keyword={selectedKeyword}
            onGenderChange={setSelectedGender}
            onCategoryChange={setSelectedCategories}
            onConditionChange={setSelectedConditions}
            onSizeChange={setSelectedSizes}
            onKeywordChange={setSelectedKeyword}
          />
        </motion.aside>

        <div className="block md:hidden">
          <FilterSheet
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            onClear={handleClearFilters}
            onApply={handleApplyFilters}
            brand={brand as Brand}
            categories={categories as Category[]}
            sizes={listingSizes as Size[]}
            selectedGender={selectedGender}
            selectedCategories={selectedCategories}
            selectedConditions={selectedConditions}
            selectedSizes={selectedSizes}
            keyword={selectedKeyword}
            onGenderChange={setSelectedGender}
            onCategoryChange={setSelectedCategories}
            onConditionChange={setSelectedConditions}
            onSizeChange={setSelectedSizes}
            onKeywordChange={setSelectedKeyword}
          />
        </div>

        <motion.main
          initial={{ marginLeft: 0 }}
          animate={{
            marginLeft: isOpen ? SIDEBAR_WIDTH : 0,
          }}
          transition={{ duration: ANIMATION_DURATION }}
          className="flex-1 w-full md:ml-0"
        >
          <div className="z-30 bg-white px-4 md:px-6">
            <ListingsHeader
              title="全ての商品"
              itemCount={totalCount}
              selectedGender={selectedGender}
              selectedCategories={selectedCategories}
              selectedSizes={selectedSizes}
              categories={categories as Category[]}
              sizes={listingSizes as Size[]}
              keyword={keyword}
            />

            <FilterSort
              isOpen={isOpen}
              onToggleFilter={() => setIsOpen(!isOpen)}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          </div>

          <ListingList
            isSidebarOpen={isOpen}
            listings={listings as Listing[]}
          />
        </motion.main>
      </div>

      <ListingPagePagination
        currentPage={pagination.currentPage}
        setCurrentPage={handlePageChange}
        totalPages={pagination.totalPages}
      />
    </div>
  )
}
